import { Link } from 'found';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { ButtonGroup, ButtonToolbar } from 'reactstrap';

import Address from 'src/components/Address';
import Loading from 'src/components/Loading';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import ProposeCommunityTradeMutation from 'src/mutations/ProposeCommunityTradeMutation';
import FlashesStore from 'src/stores/FlashesStore';

import FlatPricingForm from './FlatPricingForm';
import TimeOfUsePricingForm from './TimeOfUsePricingForm';

const TradeRuleSetCommunityPropertyFragment = graphql`
  fragment TradeRuleSetCommunity_property on Property {
    id
    title
    address {
      line1
      line2
      city
      state
      postcode
      gps {
        latitude
        longitude
      }
    }
    ...FlatPricingForm_property
    ...TimeOfUsePricingForm_property
  }
`;

const TradeRuleSetCommunityMeterFragment = graphql`
  fragment TradeRuleSetCommunity_meter on Meter {
    id
    identifier
    title
    tradePointId
    ...FlatPricingForm_meter
    ...TimeOfUsePricingForm_meter
  }
`;

/**
 * @param {object} props
 * @returns {React.ReactComponentElement} - TradeRuleSetCommunity component
 */
function TradeRuleSetCommunity(props) {
  if (!props || Object.keys(props).length === 0) {
    return <Loading />;
  }

  let { property, meter } = props;
  const { router } = props;
  const intl = useIntl();

  property = useFragment(TradeRuleSetCommunityPropertyFragment, property);
  meter = useFragment(TradeRuleSetCommunityMeterFragment, meter);

  const [timeOfUsePricing, setTimeOfUsePricing] = useState(false);
  const [processing, setProcessing] = useState(false);

  const onPricingButtonClick = () => {
    setTimeOfUsePricing(!timeOfUsePricing);
  };

  if (!property || !meter) {
    return <Loading />;
  }

  const { address, id, title } = property;
  const addr = new Address(address);

  const pageTitle = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.page_title',
    defaultMessage: 'Enosi - Property: {title} - Set community trade rules for {meterTitle}',
  }, { title, meterTitle: meter.title });

  const allTradeRulesLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.all_trade_rules.label',
    defaultMessage: 'All trade rules',
  });

  const propertySettingsLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.property_settings.label',
    defaultMessage: 'Property Settings',
  });

  const propertiesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.breadcrumbs.properties.label',
    defaultMessage: 'Properties',
  });

  const tradeRulesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.breadcrumbs.trade_rules.label',
    defaultMessage: 'Trade Rules',
  });

  const setCommunityTradeRulesBreadcrumbLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.breadcrumbs.set_community_trade_rules.label',
    defaultMessage: 'Set community trade rules for {meterTitle}',
  }, { meterTitle: meter.title });

  const setCommunityTradeRulesHeaderLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.header.set_community_trade_rules.label',
    defaultMessage: 'Set community trade rules',
  });

  const successMessage = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.response_message.success',
    defaultMessage: 'Community trade rules have been set',
  });

  const renderPropertyControls = () => (
    <ButtonToolbar>
      <ButtonGroup className="ms-2">
        <Link to={`/properties/${property.id}/trade-rules`} className="btn btn-darken">
          {allTradeRulesLabel}
        </Link>
      </ButtonGroup>

      <ButtonGroup className="ms-2">
        <Link to={`/properties/${property.id}/settings`} className="btn btn-darken">
          {propertySettingsLabel}
        </Link>
      </ButtonGroup>
    </ButtonToolbar>
  );

  const mutationName = 'proposeCommunityTrade';

  const handleTradeRuleProposeCommunitySuccess = (_response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((error) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [error],
        },
      }, mutationName));
      return;
    }

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      successMessage,
      mutationName,
    );

    router.push(`/properties/${property.id}/trade-rules/active`);
  };

  const handleTradeRuleProposeCommunityFailure = (error) => {
    setProcessing(false);

    FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
  };

  const handleTradeRuleProposeCommunity = (input) => {
    setProcessing(true);
    FlashesStore.reset();
    ProposeCommunityTradeMutation(
      input,
      handleTradeRuleProposeCommunitySuccess,
      handleTradeRuleProposeCommunityFailure,
    );
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          {
            name: propertiesBreadcrumbLabel,
            path: '/properties',
          },
          { name: title, path: `/properties/${id}` },
          {
            name: tradeRulesBreadcrumbLabel,
            path: `/properties/${id}/trade-rules/active`,
          },
          {
            name: setCommunityTradeRulesBreadcrumbLabel,
          },
        ]}
      />
      <Heading
        title={title}
        subtitle={addr.simpleString()}
        context={setCommunityTradeRulesHeaderLabel}
        controls={renderPropertyControls()}
      />

      {timeOfUsePricing ? (
        <TimeOfUsePricingForm
          property={property}
          meter={meter}
          router={router}
          onPricingButtonClick={onPricingButtonClick}
          handleTradeRuleProposeCommunity={handleTradeRuleProposeCommunity}
          processing={processing}
        />
      ) : (
        <FlatPricingForm
          property={property}
          meter={meter}
          router={router}
          onPricingButtonClick={onPricingButtonClick}
          handleTradeRuleProposeCommunity={handleTradeRuleProposeCommunity}
          processing={processing}
        />
      )}
    </>
  );
}

TradeRuleSetCommunity.propTypes = {
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

TradeRuleSetCommunity.defaultProps = {
  property: null,
  meter: null,
};

export default TradeRuleSetCommunity;
